import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, IconButton, MenuItem, Select, Stack, TextField, Tooltip, Typography, alpha,
} from '@mui/material'
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined'
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded'
import MarkChatUnreadOutlinedIcon from '@mui/icons-material/MarkChatUnreadOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, type SuperAdminFeedback } from '@cmms/api-client'
import type { FeedbackType, FeedbackStatus } from '@cmms/types'

import { UserAvatar } from '../../components/UserAvatar'
import { useConfirm } from '../../components/ConfirmDialog'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'

const TYPE_META: Record<FeedbackType, { label: string; icon: React.ReactNode; color: string }> = {
  bug:               { label: 'Bug',              icon: <BugReportRoundedIcon sx={{ fontSize: 16 }} />,  color: '#DC2626' },
  feature_request:   { label: 'Feature request',  icon: <LightbulbOutlinedIcon sx={{ fontSize: 16 }} />, color: '#7C3AED' },
  process_feedback:  { label: 'Process feedback', icon: <RouteOutlinedIcon sx={{ fontSize: 16 }} />,     color: '#D97706' },
  question:          { label: 'Question',         icon: <QuestionMarkRoundedIcon sx={{ fontSize: 16 }} />, color: '#2563EB' },
}

const STATUS_META: Record<FeedbackStatus, { label: string; color: string; bg: string }> = {
  submitted:    { label: 'Submitted',    color: '#2563EB', bg: '#EFF6FF' },
  acknowledged: { label: 'Acknowledged', color: '#D97706', bg: '#FFFBEB' },
  resolved:     { label: 'Resolved',     color: '#059669', bg: '#ECFDF5' },
}

/// Super-admin-only triage queue for Help & Support submissions —
/// aggregated across every org (feedback lives in each org's own DB;
/// there is no per-org admin view for this table). Master/detail
/// layout mirrors OrganizationsTab.
export function FeedbackTab() {
  const [activeId, setActiveId] = useState<string | null>(null)

  const feedbackQ = useQuery({
    queryKey: ['super-admin', 'feedback'],
    queryFn: () => api.superAdmin.listAllFeedback(),
  })
  const items: SuperAdminFeedback[] = Array.isArray(feedbackQ.data) ? feedbackQ.data : []
  const active = items.find((f) => f.id === activeId) ?? null

  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | 'all'>('all')
  const filtered = useMemo(
    () => statusFilter === 'all' ? items : items.filter((f) => f.status === statusFilter),
    [items, statusFilter],
  )

  if (active) {
    return <FeedbackDetail feedback={active} onBack={() => setActiveId(null)} />
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
            Help & Support submissions
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Bug reports, feature requests and feedback submitted from every workspace.
          </Typography>
        </Box>
        <Select
          size="small" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as FeedbackStatus | 'all')}
          sx={{ fontSize: 13, minWidth: 160 }}
        >
          <MenuItem value="all" sx={{ fontSize: 13 }}>All statuses</MenuItem>
          <MenuItem value="submitted" sx={{ fontSize: 13 }}>Submitted</MenuItem>
          <MenuItem value="acknowledged" sx={{ fontSize: 13 }}>Acknowledged</MenuItem>
          <MenuItem value="resolved" sx={{ fontSize: 13 }}>Resolved</MenuItem>
        </Select>
      </Stack>

      {feedbackQ.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : filtered.length === 0 ? (
        <EmptyFeedback hasAny={items.length > 0} />
      ) : (
        <Stack spacing={1}>
          {filtered.map((f) => (
            <FeedbackRow key={f.id} feedback={f} onOpen={() => setActiveId(f.id)} />
          ))}
        </Stack>
      )}
    </Box>
  )
}

function EmptyFeedback({ hasAny }: { hasAny: boolean }) {
  return (
    <Box sx={{ py: 8, textAlign: 'center' }}>
      <Box sx={{
        display: 'inline-flex', width: 56, height: 56,
        borderRadius: 99, bgcolor: 'action.hover',
        alignItems: 'center', justifyContent: 'center', mb: 2,
      }}>
        <MarkChatUnreadOutlinedIcon sx={{ fontSize: 26, color: 'text.secondary' }} />
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
        {hasAny ? 'Nothing matches this filter' : 'No submissions yet'}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>
        {hasAny ? 'Try a different status.' : 'Feedback submitted from the Help page will show up here.'}
      </Typography>
    </Box>
  )
}

function FeedbackRow({ feedback, onOpen }: { feedback: SuperAdminFeedback; onOpen: () => void }) {
  const typeMeta = TYPE_META[feedback.type]
  const statusMeta = STATUS_META[feedback.status]
  const awaitingReply = feedback.last_message_sender_type === 'user'
  return (
    <Box onClick={onOpen} sx={{
      p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper', display: 'flex', alignItems: 'center', gap: 2,
      cursor: 'pointer', transition: 'border-color 120ms ease',
      '&:hover': { borderColor: 'primary.main' },
    }}>
      <Box sx={{
        width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(typeMeta.color, 0.1), color: typeMeta.color,
      }}>
        {typeMeta.icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.005em' }} noWrap>
            {feedback.title}
          </Typography>
          {awaitingReply && (
            <Box sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: 'primary.main', flexShrink: 0 }} />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
            {feedback.org_name}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>·</Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
            {feedback.submitted_by_name ?? feedback.submitted_by_email ?? 'Unknown'}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>·</Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
            {new Date(feedback.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </Typography>
        </Stack>
      </Box>
      <Chip size="small" label={typeMeta.label} sx={{
        height: 22, fontSize: 11, fontWeight: 600,
        bgcolor: alpha(typeMeta.color, 0.1), color: typeMeta.color,
      }} />
      <Chip size="small" label={statusMeta.label} sx={{
        height: 22, fontSize: 11, fontWeight: 700,
        bgcolor: statusMeta.bg, color: statusMeta.color,
      }} />
    </Box>
  )
}

// ─── Detail panel ───────────────────────────────────────────────────────────

function FeedbackDetail({ feedback, onBack }: { feedback: SuperAdminFeedback; onBack: () => void }) {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [status, setStatus] = useState<FeedbackStatus>(feedback.status)

  const typeMeta = TYPE_META[feedback.type]

  const saveStatus = useMutation({
    mutationFn: () => api.superAdmin.updateFeedbackStatus(feedback.org_slug, feedback.id, { status }),
    onSuccess: () => {
      toast.success('Status updated')
      qc.invalidateQueries({ queryKey: ['super-admin', 'feedback'] })
    },
    onError: (e: unknown) => toast.error(e instanceof ApiError ? e.message : 'Could not update status'),
  })

  const deleteThreadM = useMutation({
    mutationFn: () => api.superAdmin.deleteFeedback(feedback.org_slug, feedback.id),
    onSuccess: () => {
      toast.success('Submission deleted')
      qc.invalidateQueries({ queryKey: ['super-admin', 'feedback'] })
      onBack()
    },
    onError: (e: unknown) => toast.error(e instanceof ApiError ? e.message : "Couldn't delete submission"),
  })

  async function handleDeleteThread() {
    const ok = await confirm({
      title: 'Delete this submission?',
      message: 'This deletes the entire conversation, including every reply. This cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger',
    })
    if (ok) deleteThreadM.mutate()
  }

  const dirty = status !== feedback.status

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button onClick={onBack} startIcon={<ArrowBackIcon fontSize="small" />}
          sx={{ fontSize: 13, color: 'text.secondary', textTransform: 'none' }}>
          All submissions
        </Button>
        <Tooltip title="Delete submission" arrow>
          <IconButton size="small" onClick={handleDeleteThread} disabled={deleteThreadM.isPending}>
            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2.5 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(typeMeta.color, 0.12), color: typeMeta.color,
        }}>
          {typeMeta.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>
            {feedback.title}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Chip size="small" label={typeMeta.label} sx={{
              height: 20, fontSize: 10.5, fontWeight: 700,
              bgcolor: alpha(typeMeta.color, 0.1), color: typeMeta.color,
            }} />
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              {feedback.org_name} · {feedback.submitted_by_name ?? feedback.submitted_by_email ?? 'Unknown submitter'}
            </Typography>
          </Stack>
        </Box>
      </Stack>

      <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2.5 }}>
        <Typography sx={{ fontSize: 13.5, color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
          {feedback.description}
        </Typography>
        {feedback.page_context && (
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 1.5, fontFamily: 'ui-monospace, monospace' }}>
            Reported from: {feedback.page_context}
          </Typography>
        )}
        {feedback.screenshot_urls.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            {feedback.screenshot_urls.map((url) => (
              <Box key={url} component="a" href={url} target="_blank" rel="noreferrer"
                sx={{ width: 84, height: 84, borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                <Box component="img" src={url} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <StatTile label="Submitted" value={new Date(feedback.created_at).toLocaleString()} />
        {feedback.resolved_at && (
          <StatTile label="Resolved" value={new Date(feedback.resolved_at).toLocaleString()} />
        )}
      </Stack>

      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.secondary', mb: 1.25 }}>
        Update status
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
        <Select size="small" value={status} onChange={(e) => setStatus(e.target.value as FeedbackStatus)} sx={{ fontSize: 13, minWidth: 200 }}>
          <MenuItem value="submitted" sx={{ fontSize: 13 }}>Submitted</MenuItem>
          <MenuItem value="acknowledged" sx={{ fontSize: 13 }}>Acknowledged</MenuItem>
          <MenuItem value="resolved" sx={{ fontSize: 13 }}>Resolved</MenuItem>
        </Select>
        <Button variant="contained" disabled={!dirty || saveStatus.isPending}
          onClick={() => saveStatus.mutate()}
          sx={{ borderRadius: 999, px: 2.5 }}>
          {saveStatus.isPending ? 'Saving…' : 'Save'}
        </Button>
      </Stack>

      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.secondary', mb: 1.25 }}>
        Conversation
      </Typography>
      <FeedbackThread feedback={feedback} typeMeta={typeMeta} />
    </Box>
  )
}

function dayLabel(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString()
  if (sameDay(d, today)) return 'Today'
  if (sameDay(d, yesterday)) return 'Yesterday'
  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric',
    year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  })
}

function DayDivider({ label }: { label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
    </Stack>
  )
}

/// Mirrors the org-user side (`HelpPage.tsx`'s conversation dialog) —
/// fixed-height scrollable message pane with a composer pinned below
/// it, day-grouped bubbles, auto-scroll on new messages. Kept as a
/// separate component here rather than a shared cross-feature import
/// since it talks to the cross-org super-admin endpoints
/// (`SuperAdminFeedback`/slug-scoped routes) instead of the per-org
/// ones the Help page uses.
function FeedbackThread({
  feedback, typeMeta,
}: {
  feedback: SuperAdminFeedback
  typeMeta: typeof TYPE_META[FeedbackType]
}) {
  const qc = useQueryClient()
  const [reply, setReply] = useState('')
  const [attachments, setAttachments] = useState<Array<{ file: File; previewUrl: string; uploadedUrl?: string; uploading: boolean }>>([])
  const scrollRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const messagesQ = useQuery({
    queryKey: ['super-admin', 'feedback', 'messages', feedback.id],
    queryFn: () => api.superAdmin.listFeedbackMessages(feedback.org_slug, feedback.id),
    // Live updates arrive over the websocket (see useChatSocket); this
    // is just a fallback in case the socket is momentarily down.
    refetchInterval: 15000,
  })
  const messages = Array.isArray(messagesQ.data) ? messagesQ.data : []

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages.length])

  async function handleFilesPicked(files: FileList | null) {
    if (!files || files.length === 0) return
    const remaining = 6 - attachments.length
    if (remaining <= 0) {
      toast.error('Up to 6 images per message')
      return
    }
    const picked = Array.from(files).slice(0, remaining)
    const entries = picked.map((file) => ({ file, previewUrl: URL.createObjectURL(file), uploading: true }))
    setAttachments((prev) => [...prev, ...entries])

    for (const entry of entries) {
      try {
        const { url } = await api.superAdmin.uploadFeedbackImage(entry.file)
        setAttachments((prev) => prev.map((a) => a === entry ? { ...a, uploadedUrl: url, uploading: false } : a))
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : "Couldn't upload image")
        setAttachments((prev) => prev.filter((a) => a !== entry))
      }
    }
  }

  function removeAttachment(entry: { previewUrl: string }) {
    setAttachments((prev) => prev.filter((a) => a.previewUrl !== entry.previewUrl))
    URL.revokeObjectURL(entry.previewUrl)
  }

  const anyUploading = attachments.some((a) => a.uploading)

  const sendM = useMutation({
    mutationFn: () => api.superAdmin.sendFeedbackMessage(feedback.org_slug, feedback.id, {
      body: reply.trim(),
      attachment_urls: attachments.map((a) => a.uploadedUrl).filter((u): u is string => !!u),
    }),
    onSuccess: () => {
      setReply('')
      attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl))
      setAttachments([])
      qc.invalidateQueries({ queryKey: ['super-admin', 'feedback', 'messages', feedback.id] })
      qc.invalidateQueries({ queryKey: ['super-admin', 'feedback'] })
    },
    onError: (e: unknown) => toast.error(e instanceof ApiError ? e.message : "Couldn't send reply"),
  })

  const canSend = (reply.trim().length > 0 || attachments.length > 0) && !anyUploading && !sendM.isPending

  const deleteMessageM = useMutation({
    mutationFn: (msgId: string) => api.superAdmin.deleteFeedbackMessage(feedback.org_slug, feedback.id, msgId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['super-admin', 'feedback', 'messages', feedback.id] }),
    onError: (e: unknown) => toast.error(e instanceof ApiError ? e.message : "Couldn't delete message"),
  })

  let lastDay = ''

  return (
    <Box sx={{
      borderRadius: 2, border: '1px solid', borderColor: 'divider',
      display: 'flex', flexDirection: 'column', height: 420,
    }}>
      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {messagesQ.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : messages.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled', fontStyle: 'italic' }}>
            No replies yet — send the first one below.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {messages.map((m) => {
              const day = dayLabel(m.created_at)
              const showDivider = day !== lastDay
              lastDay = day
              return (
                <Box key={m.id}>
                  {showDivider && <DayDivider label={day} />}
                  <ChatBubble fromMe={m.sender_type === 'super_admin'} typeColor={typeMeta.color}
                    senderLabel={m.sender_type === 'user' ? (m.sender_name ?? feedback.submitted_by_email ?? 'Submitter') : 'You'}
                    body={m.body} attachmentUrls={m.attachment_urls} timestamp={m.created_at}
                    onDelete={m.sender_type === 'super_admin' ? () => deleteMessageM.mutate(m.id) : undefined} />
                </Box>
              )
            })}
          </Stack>
        )}
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        {attachments.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {attachments.map((a) => (
              <Box key={a.previewUrl} sx={{
                position: 'relative', width: 48, height: 48,
                borderRadius: 1.25, overflow: 'hidden', border: '1px solid', borderColor: 'divider',
              }}>
                <Box component="img" src={a.previewUrl} sx={{
                  width: '100%', height: '100%', objectFit: 'cover', opacity: a.uploading ? 0.4 : 1,
                }} />
                {a.uploading && (
                  <CircularProgress size={16} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-8px', marginLeft: '-8px' }} />
                )}
                <IconButton size="small" onClick={() => removeAttachment(a)} sx={{
                  position: 'absolute', top: 1, right: 1, width: 16, height: 16,
                  bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                }}>
                  <CloseRoundedIcon sx={{ fontSize: 11 }} />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}
        <Stack direction="row" spacing={1}>
          <input ref={fileInputRef} type="file" accept="image/*" multiple hidden
            onChange={(e) => { handleFilesPicked(e.target.files); e.target.value = '' }} />
          <IconButton size="small" onClick={() => fileInputRef.current?.click()}
            disabled={attachments.length >= 6}
            sx={{ alignSelf: 'flex-end', mb: 0.25 }}>
            <AttachFileRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <TextField
            size="small" fullWidth multiline maxRows={4}
            placeholder="Reply to the submitter…"
            value={reply} onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && canSend) {
                e.preventDefault()
                sendM.mutate()
              }
            }}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 12.5 } }}
          />
          <Button
            variant="contained" size="small"
            disabled={!canSend}
            onClick={() => sendM.mutate()}
            sx={{ fontSize: 12, fontWeight: 700, borderRadius: 1.5, flexShrink: 0, alignSelf: 'flex-end' }}
          >
            Send
          </Button>
        </Stack>
      </Box>
    </Box>
  )
}

/// Same bubble visuals as `HelpPage.tsx`'s `ChatBubble` — duplicated
/// rather than shared for the same reason `FeedbackThread` is: the
/// surrounding data shape differs between the org-user and
/// super-admin sides.
function ChatBubble({
  fromMe, senderLabel, body, attachmentUrls, timestamp, typeColor, onDelete,
}: {
  fromMe: boolean
  senderLabel: string
  body: string
  attachmentUrls?: string[]
  timestamp: string
  typeColor: string
  onDelete?: () => void
}) {
  return (
    <Stack direction={fromMe ? 'row-reverse' : 'row'} spacing={1} alignItems="flex-end"
      sx={{ '&:hover .chat-bubble-delete': { opacity: 1 } }}>
      <UserAvatar name={senderLabel} size={26}
        sx={{ flexShrink: 0, bgcolor: fromMe ? typeColor : '#64748B' }} />
      {onDelete && (
        <Tooltip title="Delete message" arrow>
          <IconButton size="small" onClick={onDelete} className="chat-bubble-delete"
            sx={{ opacity: 0, transition: 'opacity 120ms ease', alignSelf: 'center', width: 22, height: 22 }}>
            <DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ maxWidth: '76%' }}>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 700, color: 'text.disabled', mb: 0.35,
          textAlign: fromMe ? 'right' : 'left',
        }}>
          {senderLabel} · {new Date(timestamp).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
        </Typography>
        <Box sx={{
          px: 1.5, py: 1, borderRadius: 2,
          bgcolor: fromMe ? alpha(typeColor, 0.1) : 'action.hover',
          border: '1px solid', borderColor: fromMe ? alpha(typeColor, 0.25) : 'divider',
        }}>
          {attachmentUrls && attachmentUrls.length > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: body ? 0.75 : 0 }}>
              {attachmentUrls.map((url) => (
                <Box key={url} component="a" href={url} target="_blank" rel="noreferrer"
                  sx={{ width: 72, height: 72, borderRadius: 1.25, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  <Box component="img" src={url} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              ))}
            </Stack>
          )}
          {body && (
            <Typography sx={{ fontSize: 12.5, color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
              {body}
            </Typography>
          )}
        </Box>
      </Box>
    </Stack>
  )
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{
      p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  )
}
